import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { DesafioMfa, OperadorSesion, PermisoOperador } from '@cmms/shared';
import { operadorPuede } from '@cmms/shared';
import { api, ErrorApi, registrarSesionExpirada } from './api.js';
import { notificarErrorGlobal } from '../componentes/notificaciones-globales.js';

interface Sesion {
  operador: OperadorSesion | null;
  cargando: boolean;
  /** Devuelve el desafío cuando la cuenta aún no inscribió su segundo factor. */
  iniciar: (email: string, contrasena: string, codigo?: string) => Promise<DesafioMfa | null>;
  inscribirMfa: (email: string, contrasena: string) => Promise<DesafioMfa>;
  confirmarMfa: (email: string, contrasena: string, codigo: string) => Promise<void>;
  salir: () => Promise<void>;
  puede: (permiso: PermisoOperador) => boolean;
}

const ContextoOperador = createContext<Sesion | null>(null);

/**
 * Sesión de la consola de plataforma, deliberadamente separada de la de tenant.
 *
 * Son dos proveedores, dos juegos de cookies y dos rutas de renovación. Un
 * operador puede tener abierta a la vez su sesión de plataforma y una de alguna
 * organización sin que una pise a la otra, y —más importante— cerrar una no
 * afecta a la otra ni le presta credenciales.
 */
export function ProveedorSesionOperador({ children }: { children: ReactNode }): JSX.Element {
  const [operador, setOperador] = useState<OperadorSesion | null>(null);
  const [cargando, setCargando] = useState(true);

  const operadorRef = useRef(operador);
  operadorRef.current = operador;

  useEffect(() => {
    registrarSesionExpirada('plataforma', () => {
      if (operadorRef.current === null) return;
      operadorRef.current = null;
      setOperador(null);
      notificarErrorGlobal('Tu sesión expiró. Vuelve a iniciar sesión.');
    });
    return () => registrarSesionExpirada('plataforma', null);
  }, []);

  useEffect(() => {
    let vigente = true;

    void (async () => {
      try {
        setOperador(await perfilConRenovacion());
      } catch {
        if (vigente) setOperador(null);
      } finally {
        if (vigente) setCargando(false);
      }
    })();

    return () => {
      vigente = false;
    };
  }, []);

  const iniciar = useCallback(async (email: string, contrasena: string, codigo?: string) => {
    const respuesta = await api.post<{ operador: OperadorSesion } | DesafioMfa>('/plataforma/auth/sesion', {
      email,
      contrasena,
      ...(codigo ? { codigo } : {}),
    });

    if ('mfaPendiente' in respuesta) return respuesta;
    setOperador(respuesta.operador);
    return null;
  }, []);

  const inscribirMfa = useCallback(
    (email: string, contrasena: string) =>
      api.post<DesafioMfa>('/plataforma/auth/mfa/inscribir', { email, contrasena }),
    [],
  );

  const confirmarMfa = useCallback(async (email: string, contrasena: string, codigo: string) => {
    const respuesta = await api.post<{ operador: OperadorSesion }>('/plataforma/auth/mfa/confirmar', {
      email,
      contrasena,
      codigo,
    });
    setOperador(respuesta.operador);
  }, []);

  const salir = useCallback(async () => {
    await api.post('/plataforma/auth/salir').catch((error: unknown) => {
      if (!(error instanceof ErrorApi)) throw error;
    });
    setOperador(null);
  }, []);

  const valor = useMemo<Sesion>(
    () => ({
      operador,
      cargando,
      iniciar,
      inscribirMfa,
      confirmarMfa,
      salir,
      // Se consulta también aquí para no ofrecer botones que el servidor va a
      // rechazar; la decisión real la sigue tomando el guard.
      puede: (permiso) => (operador ? operadorPuede(operador.rol, permiso) : false),
    }),
    [operador, cargando, iniciar, inscribirMfa, confirmarMfa, salir],
  );

  return <ContextoOperador.Provider value={valor}>{children}</ContextoOperador.Provider>;
}

export function useSesionOperador(): Sesion {
  const contexto = useContext(ContextoOperador);
  if (!contexto) throw new Error('useSesionOperador debe usarse dentro de ProveedorSesionOperador');
  return contexto;
}

/**
 * Restaura la sesión al cargar la consola.
 *
 * El acceso de operador dura diez minutos, así que al volver a la pestaña lo
 * habitual es encontrarlo vencido con el refresco todavía vivo. El cliente
 * genérico no reintenta las rutas de sesión —allí un 401 son credenciales
 * malas—, de modo que aquí la renovación se pide una vez y a mano.
 */
async function perfilConRenovacion(): Promise<OperadorSesion> {
  try {
    return await api.get<OperadorSesion>('/plataforma/auth/yo');
  } catch (error) {
    if (!(error instanceof ErrorApi) || error.estado !== 401) throw error;
    await api.post('/plataforma/auth/renovar');
    return api.get<OperadorSesion>('/plataforma/auth/yo');
  }
}
